import { isNil } from 'lodash';
import { useSelector } from 'react-redux';

import { validReports } from '@modules/reports/basicReports/constants';
import { NormalizedReportItem } from '@modules/reports/basicReports/duck/utils';
import {
  useGetReportListByOrgQuery,
  useGetReportListByPGQuery,
  useGetReportListByUserQuery,
} from '@modules/rtk/webscript-api';
import { isParentOrYouthMemberSel } from '@modules/shared/duck';
import { youthPersonGuidSel } from '@modules/youthProfile/duck/selectors';
import {
  isParentGuardianSel,
  organizationGuidSel,
  personGuidSel,
  unitTypeIdSel,
  youthMemberPersonGuidSel,
} from '@shared/duck/selectors/general.selectors';

export function useGetBasicReports(overwriteOrgGuid?: string | null) {
  const organizationGuid: string = useSelector(organizationGuidSel);
  const youthMemberPersonGuid = useSelector(
    youthMemberPersonGuidSel,
  ) as unknown as string;
  const PGPersonGuid = useSelector(personGuidSel) as string;
  const unitTypeId = useSelector(unitTypeIdSel);

  const isParentOrYouthMember = useSelector(isParentOrYouthMemberSel);
  const isParentGuardian = useSelector(isParentGuardianSel);

  const { data: reportDataByOrg, isLoading: isLoadingReportByOrg } =
    useGetReportListByOrgQuery(
      { params: { OrganizationGUID: overwriteOrgGuid || organizationGuid } },
      { skip: isParentOrYouthMember || !organizationGuid },
    );

  const { data: reportDataByUser, isLoading: isLoadingReportByUser } =
    useGetReportListByUserQuery(
      { params: { PersonGUID: youthMemberPersonGuid } },
      {
        skip:
          !isParentOrYouthMember || !organizationGuid || !youthMemberPersonGuid,
      },
    );

  const { data: reportDataByPG, isLoading: isLoadingReportByPG } =
    useGetReportListByPGQuery(
      { params: { PersonGUID: PGPersonGuid } },
      { skip: !isParentGuardian },
    );

  if (isParentOrYouthMember ? isNil(reportDataByUser) : isNil(reportDataByOrg))
    return {
      loading: isLoadingReportByOrg || isLoadingReportByUser,
      reports: [] as NormalizedReportItem[],
      organizationGuid: '',
    };

  const reports = isParentOrYouthMember ? reportDataByUser : reportDataByOrg;

  let filteredReports = reports?.reports ?? [];

  if (!isNil(reportDataByPG?.reports)) {
    filteredReports = [...filteredReports, ...reportDataByPG.reports];
  }

  const validReportCodes: string[] =
    validReports[Number(unitTypeId) as keyof typeof validReports];

  if (isNil(overwriteOrgGuid) && validReportCodes) {
    filteredReports = filteredReports.filter(({ reportCode }) =>
      validReportCodes.includes(reportCode),
    );
  }

  return {
    loading:
      isLoadingReportByOrg || isLoadingReportByUser || isLoadingReportByPG,
    reports: filteredReports,
    personGuid: youthMemberPersonGuid || '',
    organizationGuid: organizationGuid || '',
  };
}

export function useAdditionalUserReports() {
  const youthPersonGuid: string = useSelector(youthPersonGuidSel);
  const isParentGuardian = useSelector(isParentGuardianSel);
  const organizationGuid: string = useSelector(organizationGuidSel);
  const PGPersonGuid = useSelector(personGuidSel) as string;

  const { data: reportDataByUser, isLoading: isLoadingReportByUser } =
    useGetReportListByUserQuery(
      {
        params: {
          PersonGUID: youthPersonGuid,
          OrganizationGUID: organizationGuid,
        },
      },
      {
        skip: !youthPersonGuid,
      },
    );

  const { data: reportDataByPG, isLoading: isLoadingReportByPG } =
    useGetReportListByPGQuery(
      { params: { PersonGUID: PGPersonGuid } },
      { skip: !isParentGuardian },
    );

  if (isNil(reportDataByUser))
    return {
      loading: isLoadingReportByUser,
      reports: [] as NormalizedReportItem[],
    };

  let filteredReports = reportDataByUser?.reports ?? [];

  if (!isNil(reportDataByPG?.reports)) {
    filteredReports = [...filteredReports, ...reportDataByPG.reports];
  }

  return {
    loading: isLoadingReportByUser || isLoadingReportByPG,
    reports: filteredReports,
    personGuid: youthPersonGuid,
  };
}
